import { toast } from "sonner";
import type { ApiProblem } from "@ticketnjoy/types";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5258";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public detail: string,
    public fieldErrors?: Record<string, string[]>,
    public traceId?: string,
  ) {
    super(detail || code);
    this.name = "ApiError";
  }
}

function problemToApiError(status: number, payload: unknown): ApiError {
  const problem = (payload ?? {}) as ApiProblem;
  const firstFieldError = problem.errors
    ? Object.values(problem.errors).flat()[0]
    : undefined;
  return new ApiError(
    status,
    problem.title ?? "unknown_error",
    problem.detail ?? firstFieldError ?? "Beklenmeyen bir hata oluştu.",
    problem.errors,
    problem.traceId,
  );
}

export function showApiError(err: unknown, fallback = "Bir şeyler ters gitti."): void {
  if (!(err instanceof ApiError)) {
    toast.error(fallback);
    return;
  }
  if (err.status === 429) {
    toast.error("Çok fazla deneme yapıldı. Biraz sonra tekrar dene.");
    return;
  }
  if (err.status >= 500 && err.traceId) {
    toast.error(err.detail || fallback, {
      description: `Destek için kod: ${err.traceId}`,
      duration: 10000,
    });
    return;
  }
  toast.error(err.detail || fallback);
}

export interface ApiClientOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/** Admin oturum cookie'si HttpOnly + Path=/admin; `credentials: include` ile gider. */
export async function apiClient<T = unknown>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = options;
  // FormData'da boundary'li Content-Type'ı tarayıcı yazar.
  const isForm = body instanceof FormData;

  const init: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Locale": "tr",
      ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...rest,
  };
  if (body !== undefined) {
    init.body = isForm ? body : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, init);
  } catch {
    throw new ApiError(0, "network_error", "Sunucuya ulaşılamadı.");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") ||
    contentType.includes("application/problem+json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw problemToApiError(response.status, payload);
  }
  return payload as T;
}
